import React from 'react';
import { Form, Field } from 'react-final-form';
import createDecorator from 'final-form-calculate';
import {
  FormGroup,
  InputGroup,
  Button,
  ButtonGroup,
  ControlLabel,
  FormControl,
  HelpBlock,
  Table,
} from 'react-bootstrap';
import Select from 'react-select';
import BidToolbar from './BidToolbar';
import { required, validRate, validPercentage, positiveNumber } from '../../../forms';

const SelectInput = ({
  input,
  options,
  name,
  id,
  label,
  help,
  meta: { active, touched, error, warning },
}) => (
  <FormGroup>
    <ControlLabel>{label}</ControlLabel>
    <Select
      id={id}
      name={name}
      options={options}
      value={input.value}
      onChange={value => {
        input.onChange(value.value);
      }}
      onBlur={value => input.onBlur(value.value)}
      joinValues
    />
    {help && <HelpBlock>{help}</HelpBlock>}
    {touched &&
      ((error && <p className="alert alert-danger">{error}</p>) ||
        (warning && <p className="alert alert-warning">{warning}</p>))}
  </FormGroup>
);

const currencyOptions = [
  {
    label: '🇦🇺 AUD - Australian Dollar',
    value: 'AUD',
  },
  {
    label: '🇨🇦 CAD - Canadian Dollar',
    value: 'CAD',
  },
  {
    label: '🇨🇳 CNY - Chinese Yuan Renminbi',
    value: 'CNY',
  },
  {
    label: '🇪🇺 EUR - Euro',
    value: 'EUR',
  },
  {
    label: '🇬🇧 GBP - British Pound',
    value: 'GBP',
  },
  {
    label: '🇯🇵 JPY - Japanese Yen',
    value: 'JPY',
  },
  {
    label: '🇲🇽 MXN - Mexican Peso',
    value: 'MXN',
  },
  {
    label: '🇺🇸 USD - US Dollar',
    value: 'USD',
  },
];

const getValidationStyle = (touched, errorMsg) => {
  if (!errorMsg || !touched) {
    return '';
  }
  return 'error';
};

const getBorderStyle = (touched, errorMsg) => {
  const style = {
    border: '1px solid #ccc',
    borderBottom: 'none',
    textAlign: 'right',
    paddingRight: '10px',
  };
  if (touched && errorMsg) {
    style.border = '1px solid #a94442';
    style.borderBottom = '1px solid #a94442';
  }
  return style;
};

export const MoneyInput = ({
  input,
  label,
  type,
  placeholder,
  meta: { touched, error, warning },
}) => (
  <FormGroup validationState={getValidationStyle(touched, error)}>
    <InputGroup
      style={{
        display: 'table',
        margin: 0,
      }}
    >
      <InputGroup.Addon>$</InputGroup.Addon>
      <FormControl
        {...input}
        type={type}
        style={getBorderStyle(touched, error)}
        placeholder={placeholder}
      />
    </InputGroup>
  </FormGroup>
);

export const PercentageInput = ({ input, label, type, meta: { touched, error, warning } }) => (
  <FormGroup validationState={getValidationStyle(touched, error)}>
    <InputGroup
      style={{
        display: 'table',
        width: '50%',
      }}
    >
      <InputGroup.Addon>{label}</InputGroup.Addon>
      <FormControl {...input} type={type} style={getBorderStyle(touched, error)} />
      <InputGroup.Addon>%</InputGroup.Addon>
    </InputGroup>
    <FormControl.Feedback />
  </FormGroup>
);

export const LabeledInput = ({ input, label, placeholder, help, type, meta }) => (
  <InputGroup>
    <ControlLabel>{label}</ControlLabel>
    <FormControl {...input} type={type} placeholder={placeholder} />
    {help && <HelpBlock>{help}</HelpBlock>}
    {meta.touched &&
      ((meta.error && (
        <div>
          <span className="danger">{meta.error}</span>
        </div>
      )) ||
        (meta.warning && (
          <div>
            <span className="danger">{meta.warning}</span>
          </div>
        )))}
  </InputGroup>
);
export const ReadOnlyInput = ({ input, label, placeholder, help, type, meta }) => (
  <InputGroup>
    <ControlLabel>{label}</ControlLabel>
    <FormControl {...input} type={type} placeholder={placeholder} readOnly />
    {help && <HelpBlock>{help}</HelpBlock>}
    {meta.touched &&
      ((meta.error && (
        <div>
          <span className="danger">{meta.error}</span>
        </div>
      )) ||
        (meta.warning && (
          <div>
            <span className="danger">{meta.warning}</span>
          </div>
        )))}
  </InputGroup>
);

export const HiddenInput = ({ input, label, placeholder, help, type, meta }) => (
  <InputGroup>
    <FormControl {...input} type="hidden" />
  </InputGroup>
);

const NoteInput = ({ input, label, type, help, meta }) => (
  <FormGroup>
    <ControlLabel>Note</ControlLabel>
    <FormControl
      {...input}
      componentClass="textarea"
      name="note"
      placeholder="Add a note about your offer."
    />
    {help && <HelpBlock>{help}</HelpBlock>}
  </FormGroup>
);

const composeValidators = (...validators) => value =>
  validators.reduce((error, validator) => error || validator(value), undefined);

const calculator = createDecorator({
  field: /\S*_rate/, // when a field matching this pattern changes...
  updates: {
    // ...update the total to the result of this function
    total: (ignoredValue, allValues) => {
      let sum = 0;
      if (allValues.suites) {
        sum += Number(allValues.suite_rate || 0) * allValues.suites * allValues.nights;
      }
      if (allValues.kings) {
        sum += Number(allValues.king_rate || 0) * allValues.kings * allValues.nights;
      }
      if (allValues.queens) {
        sum += Number(allValues.queen_rate || 0) * allValues.queens * allValues.nights;
      }
      if (allValues.hotel.tax_rate) {
        sum += Number(allValues.hotel.tax_rate / 100.0 || 0) * sum;
      }
      return sum;
    },
  },
});

const parsePercent = value => {
  if (!value || value.charAt(value.length - 1) === '.') return value;
  return typeof value === 'number' ? value : parseFloat(value.replace(/[^\d.]/g, '')).toFixed(3);
};

const formatMoney = (value, currency) => {
  if (!value) return value;
  // const onlyNums = value.replace(/[^\d]/g, '');
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    minimumFractionDigits: 2,
    currency,
  });
  return formatter.format(value);
};
// const normalizePercent = (value) => {
//   const onlyNums = value.replace(/[^\d]/g, '');
//   return parseFloat(onlyNums) / 100;
// };

const Error = ({ name }) => (
  <Field
    name={name}
    subscription={{ touched: true, error: true }}
    render={({ meta: { touched, error } }) =>
      touched && error ? <span className="alert-danger">{error}</span> : null
    }
  />
);

const BidDetailForm = ({
  onSubmit,
  initialValues,
  bid,
  displayBidAssignment,
  closeBidAssignment,
  editBid,
  isNew,
}) => (
  <Form
    onSubmit={onSubmit}
    initialValues={initialValues}
    decorators={[calculator]}
    validate={values => {
      const errors = {};
      if (!values.hotel) {
        if (!values.hotel.tax_rate) {
          errors.hotel.tax_rate = 'Please set the combined tax rate.';
        }
      }
      return errors;
    }}
    render={({ handleSubmit, reset, submitting, pristine, errors, values }) => (
      <form onSubmit={handleSubmit}>
        <Field
          name="hotel.currency"
          type="text"
          component={SelectInput}
          placeholder="USD"
          label="Currency"
          options={currencyOptions}
        />
        <Error name="hotel.currency" />
        <Field
          name="hotel.tax_rate"
          type="text"
          component={PercentageInput}
          placeholder="12.0%"
          label="Tax Rate"
          // help="Total estimated tax rate (as a percent)"
          validate={composeValidators(required, validPercentage)}
        />
        <Error name="hotel.tax_rate" />
        <Field name="nights" type="text" component={HiddenInput} disabled label="Nights" />
        <Table responsive striped bordered condensed hover className="hotel-bid-table">
          <thead>
            <tr>
              <td>
                <strong>Room Type</strong>
              </td>
              <td>
                <strong>Number of Rooms</strong>
              </td>
              <td>
                <strong>Rate Per Night</strong>
              </td>
            </tr>
          </thead>
          <tbody>
            {values.suites !== 0 && (
              <tr>
                <td>Suite</td>
                <td>{values.suites}</td>
                <td>
                  <Field
                    name="suite_rate"
                    type="number"
                    component={MoneyInput}
                    label="Suites"
                    placeholder="99.99"
                    validate={composeValidators(required, validRate)}
                  />
                  <Error name="suite_rate" />
                </td>
              </tr>
            )}
            {values.kings !== 0 && (
              <tr>
                <td>King</td>
                <td>{values.kings}</td>
                <td>
                  <Field
                    name="king_rate"
                    type="number"
                    component={MoneyInput}
                    label="Kings"
                    placeholder="99.99"
                    validate={composeValidators(required, validRate)}
                  />
                  <Error name="king_rate" />
                </td>
              </tr>
            )}
            {values.queens !== 0 && (
              <tr>
                <td>Double Room</td>
                <td>{values.queens}</td>
                <td>
                  <Field
                    name="queen_rate"
                    type="number"
                    component={MoneyInput}
                    label="Double Room"
                    placeholder="99.99"
                    validate={composeValidators(required, validRate)}
                  />
                  <Error name="queen_rate" />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        <Field
          name="commission_rate"
          type="text"
          component={PercentageInput}
          placeholder="10%"
          label="Commission"
          validate={composeValidators(required, validPercentage)}
        />
        <Error name="commission_rate" />
        <Field
          name="total"
          type="text"
          readOnly
          component={ReadOnlyInput}
          placeholder="Enter rates for this value to be calculated."
          label="Total"
          help="Estimated total including tax"
          format={v => formatMoney(v, values.hotel.currency)}
        />
        {isNew && (
          <Field
            name="note"
            type="textarea"
            component={NoteInput}
            label="Note"
            help="Please enter pricing and commission in appropriate boxes. Do not put commission in notes area. Use this area to clarify offering or to ask questions."
          />
        )}
        <div className="form-group">
          <ButtonGroup>
            <Button disabled={pristine || submitting} bsStyle="success" type="submit">
              {isNew ? 'Submit Bid' : 'Update Bid'}
            </Button>
            <Button disabled={pristine || submitting} bsStyle="success" onClick={reset}>
              Reset
            </Button>{' '}
            <BidToolbar bid={bid} editBid={editBid} displayBidAssignment={displayBidAssignment} />
          </ButtonGroup>
        </div>
      </form>
    )}
  />
);

export default BidDetailForm;



// WEBPACK FOOTER //
// src/routes/booking/components/BidDetailForm.js